'use client';

import { LazyMotion, MotionConfig } from 'framer-motion';
import { useEffect, useState } from 'react';

import { shouldReduceMotion } from '@infor/utils/src/should-reduce-motion';

// ----------------------------------------------------------------------

export type MotionLazyProps = {
  children: React.ReactNode;
  /**
   * Quando true, desativa microanimações em hardware modesto
   * e quando o SO pede prefers-reduced-motion.
   * Opt-in por aplicação (default: false).
   */
  adaptToModestHardware?: boolean;
};

const REDUCE_MOTION_DATASET_KEY = 'reduceMotion';

const loadFeaturesAsync = async () =>
  import('./features').then((res) => res.default);

export function MotionLazy({
  children,
  adaptToModestHardware = false,
}: MotionLazyProps) {
  const [shouldReduce, setShouldReduce] = useState(() =>
    adaptToModestHardware ? shouldReduceMotion() : false
  );

  useEffect(() => {
    if (!adaptToModestHardware) {
      setShouldReduce(false);

      return;
    }

    const nextShouldReduce = shouldReduceMotion();

    setShouldReduce(nextShouldReduce);
  }, [adaptToModestHardware]);

  useEffect(() => {
    if (!adaptToModestHardware || !shouldReduce) {
      delete document.documentElement.dataset[REDUCE_MOTION_DATASET_KEY];

      return;
    }

    document.documentElement.dataset[REDUCE_MOTION_DATASET_KEY] = 'true';

    return () => {
      delete document.documentElement.dataset[REDUCE_MOTION_DATASET_KEY];
    };
  }, [adaptToModestHardware, shouldReduce]);

  const content = (
    <LazyMotion strict features={loadFeaturesAsync}>
      {children}
    </LazyMotion>
  );

  if (!adaptToModestHardware) {
    return content;
  }

  return (
    <MotionConfig reducedMotion={shouldReduce ? 'always' : 'user'}>
      {content}
    </MotionConfig>
  );
}
